import React, { useMemo, useState } from 'react';
import { Box, Typography, Container, Grid, Card, CardContent, Chip, Button, Dialog, DialogTitle, DialogContent, IconButton, Divider } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GraduationCap, Code2, Database, BrainCircuit, Layers, BookOpen,
  Youtube, FileText, Download, Lock, Unlock, ExternalLink, Sparkles, Radio, X,
} from 'lucide-react';

const REGISTRATION_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLScEWWnKmrB0KRsrxs1YU7AoZxwD2zsYshqmt6wGp35zAIIusA/viewform?usp=dialog';

/* ─── Types ───────────────────────────────────────────── */
type ChapterSection = {
  heading: string;
  paragraphs?: string[];
  trailingParagraphs?: string[];
  bulletsSimple?: string[];
  bulletsTrailing?: string[];
  bullets?: { title: string; body: string; subBullets?: string[] }[];
  note?: string;
  code?: string;
};
type Chapter = { sections: ChapterSection[] };

type Topic = {
  name: string;
  desc: string;
  video?: string;       // YouTube / lesson video link
  video2?: string;      // optional second video link
  material?: string;    // student material / notes link
  assignment?: string;  // downloadable assignment link
  colab?: string;       // Google Colab notebook link
  colab2?: string;      // optional second Colab link
  sourceDocument?: string;
  dockerCompose?: string;
  embedderFile?: string;
  requirementsFile?: string;
  chapterTitle?: string;
  chapter?: Chapter;    // rich "Read Chapter" content
};
type Module = {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  icon: React.ComponentType<{ size?: number; color?: string }>;
  accent: string;
  bg: string;
  topics: Topic[];
};

/* ─── Course Data ─────────────────────────────────────── */
const modules: Module[] = [
  {
    id: 'python',
    title: 'Python',
    subtitle: 'Programming Foundations',
    description:
      'Master Python from the ground up — data types, collections, control flow and advanced concepts like classes, iterators, generators and decorators.',
    icon: Code2,
    accent: '#3776AB',
    bg: '#EAF2FF',
    topics: [
      { name: 'Data, Datatypes & Variables', desc: 'How data is represented, the built-in datatypes (int, float, str, bool, complex), and variables that store values in memory.', video: 'https://youtu.be/vPzBjxMZQdc', material: 'https://docs.google.com/document/d/1nIBV1CJ5IwyJP2IT8ux4rx3FRn4hNfL1XnKEVc8X_ms/edit?usp=sharing', assignment: 'https://docs.google.com/document/d/1VWFXaadLf0ij85gxEhtWf3Avrbn7KCeW/edit?usp=sharing&ouid=107314290516657778545&rtpof=true&sd=true', colab: 'https://colab.research.google.com/drive/1J89UlI2TE89nItjsqfvpePj2OOqqJi7e?usp=sharing' },
      { name: 'Strings', desc: 'Text data — slicing, formatting and built-in string methods.', video: 'https://youtu.be/JqTAldQ8tXk', material: 'https://docs.google.com/document/d/1VfgLrZss8FL-_L1LQm1u9C3sWGlVMZ7mlEDBf3N96gY/edit?usp=drive_link', assignment: 'https://docs.google.com/document/d/16Xfx57DbII56EzL12CJeAI_JJdfa-0JX-rK57HSBIwQ/edit?usp=sharing', colab: 'https://colab.research.google.com/drive/1AM7zjaXH1-WU_tLw2JsFvm4_NVXIrtF0?usp=sharing' },
      { name: 'Tuple', desc: 'Ordered, immutable collection of items.', video: 'https://youtu.be/wUF8nj_-QKY', material: 'https://docs.google.com/document/d/1LYphw-06Di6fb3kw7-VSU9hsZ7ewAg01_Yiqw1LEZ-I/edit?usp=sharing', assignment: 'https://docs.google.com/document/d/1lkmEBafBVNKuvUPfJ3MDVJ0lXXxn9Hb4xb6taQcX1Ko/edit?usp=sharing', colab: 'https://colab.research.google.com/drive/1jWbsurtqjmxh3EDkjB4oAGPgIknuwMWi?usp=sharing' },
      { name: 'List', desc: 'Ordered, mutable collection you can grow and change.', video: 'https://youtu.be/86PNwWJFp5c', video2: 'https://youtu.be/-GGqBS1sIqw', material: 'https://docs.google.com/document/d/1QFL4ETJl7W1-pVRBR0mV9g-7JqIe6rwW3dCQo1Uwoto/edit?usp=drive_link', assignment: 'https://docs.google.com/document/d/1hJEdzWcwZ3wbQw6wNuWE1_OIVX9Sw8P1k6SExmcBpXM/edit?usp=drive_link' },
      { name: 'Dictionary', desc: 'Key–value pairs for fast, labelled lookups.', video: 'https://youtu.be/V0nAWjiPkrE', material: 'https://docs.google.com/document/d/1y6p_zAEtmcsPebYT3P5kGwxkNEdRGSHVTALKZpQr9Pw/edit?usp=sharing', assignment: 'https://docs.google.com/document/d/1r3X_KQz6YRy91eef-nwrYQ0E4xD2xgxnMdSAZAbsZD0/edit?usp=sharing' },
      { name: 'Sets', desc: 'Unordered collection of unique items.', video: 'https://youtu.be/_apQnxX-81A', material: 'https://docs.google.com/document/d/1GUiUgv-PhZtnbJHHeIyHHgBLAd21Qaaziy4YMDpP_IY/edit?usp=sharing', assignment: 'https://docs.google.com/document/d/1GUiUgv-PhZtnbJHHeIyHHgBLAd21Qaaziy4YMDpP_IY/edit?usp=sharing', colab: 'https://colab.research.google.com/drive/1XBC8s4gORMwHjFWi7o9YKuW_YO1KwHf0?usp=sharing' },
      { name: 'Conditional Statements', desc: 'if / elif / else to make decisions in code.', video: 'https://www.youtube.com/watch?v=Vq6Uue1JQrA&list=PLAkLwSejqCKRXFm04uozw6ZWUGBaxs8g3&index=9', material: 'https://docs.google.com/document/d/1KvS7BbuXwru1HrkHcJeI4JkIrEJNUY4zHy1MCv9CuIQ/edit?usp=drive_link', assignment: 'https://docs.google.com/document/d/1qHDXC23EErMTiPG3VvGAZ52kVYYENDL4-YB1uofSbT4/edit?usp=drive_link' },
      { name: 'Functions', desc: 'Reusable blocks of code with arguments and return values.', video: 'https://youtu.be/rkQQjUClojE', material: 'https://docs.google.com/document/d/1l711JzHDq3bxAS6YQKGKoKRJoSiEGiQulBRATq10i6M/edit?usp=sharing', assignment: 'https://docs.google.com/document/d/1bPAui2wWOPakG-2ftl2AfvbXUlfi_suj2Ah3o9ZfCqI/edit?usp=sharing', colab: 'https://colab.research.google.com/drive/1KN64_vXPK9Gye0-BU3GIZ2pwCZr3648y?usp=sharing' },
      { name: 'Classes & Objects', desc: 'Object-oriented programming — blueprints and their instances.' },
      { name: 'Iterators', desc: 'Objects that yield items one at a time using __next__.' },
      { name: 'Generators', desc: 'Lazy iterators built with yield for memory efficiency.' },
      { name: 'Decorators', desc: 'Functions that wrap and extend other functions.' },
    ],
  },
  {
    id: 'database',
    title: 'Database',
    subtitle: 'MySQL & SQL Queries',
    description:
      'Learn to design and query relational databases with MySQL — creating tables, reading and modifying data, joins, pattern matching and aggregation.',
    icon: Database,
    accent: '#10B981',
    bg: '#ECFDF5',
    topics: [
      { name: 'Introduction to Database, DBMS & SQL', desc: 'What data is, why databases are needed, data persistence, variables vs Excel vs database, types of databases, DBMS, and the SQL command categories — DDL, DML, DCL, TCL and DQL.', video: 'https://youtu.be/H1VqZLwaslg', material: 'https://docs.google.com/document/d/1JMfT9eJR3HQY1LlH9eoB-s5llOJbgIOao8hYB-BjtVs/edit?usp=sharing', assignment: 'https://docs.google.com/document/d/1tf2e9Ug0oNouq3RbjzrDkQEh5eDvs9ZViAiK0CLtelY/edit?usp=drive_link' },
      { name: 'Creating Your First Database', desc: 'The MySQL Workbench interface, connecting to the MySQL server, the SQL editor, CREATE DATABASE, refreshing the schema navigator and database naming best practices.', video: 'https://youtu.be/nDT4-M5SOaY', material: 'https://docs.google.com/document/d/1JMfT9eJR3HQY1LlH9eoB-s5llOJbgIOao8hYB-BjtVs/edit?usp=sharing', assignment: 'https://docs.google.com/document/d/1tf2e9Ug0oNouq3RbjzrDkQEh5eDvs9ZViAiK0CLtelY/edit?usp=drive_link' },
      { name: 'Creating Tables & DESCRIBE', desc: 'Select an existing database, create a table with CREATE TABLE, understand columns and data types, then inspect structure with DESCRIBE, SHOW DATABASES and SHOW TABLES.', video: 'https://youtu.be/pgZwgevMpf0', material: 'https://docs.google.com/document/d/1KAarW8DVXKTfGFh9pQlIPton7493s-AX0yEl7IZoRR8/edit?usp=sharing', assignment: 'https://docs.google.com/document/d/1zECspm3S5gFgwohmno-GZY4EBFlt4VSnbRg8qzufghs/edit?usp=sharing' },
      { name: 'MySQL Constraints', desc: 'Rules enforced on table columns — NOT NULL, UNIQUE, PRIMARY KEY, FOREIGN KEY, DEFAULT and CHECK — that keep your data valid and consistent.', video: 'https://youtu.be/EKR-jzc-1dQ', material: 'https://docs.google.com/document/d/11qVwRhxhYPCFGn1YZBUQtz2ThvVKXL4UqsaXi2GhaTc/edit?usp=sharing' },
      { name: 'SELECT', desc: 'Query and read data from one or more tables.' },
      { name: 'UPDATE', desc: 'Modify existing rows with new values.' },
      { name: 'DELETE', desc: 'Remove rows from a table safely.' },
      { name: 'AND / OR', desc: 'Combine multiple conditions in a WHERE clause.' },
      { name: 'LIKE', desc: 'Search for a specified pattern within a column.' },
      { name: 'Wildcard Characters', desc: '% and _ for flexible pattern matching.' },
      { name: 'IN', desc: 'Match a value against a supplied list of values.' },
      { name: 'Aggregate Functions', desc: 'COUNT, SUM, AVG, MIN, MAX over groups of rows.' },
      { name: 'Joins', desc: 'Combine rows across tables — INNER, LEFT, RIGHT.' },
    ],
  },
  {
    id: 'ai',
    title: 'Artificial Intelligence',
    subtitle: 'LLMs, Prompting, Memory & RAG',
    description:
      'A guided day-by-day track — from your first LLM and chat models through prompting, conversational memory, RAG theory and building a vector-DB embedding pipeline.',
    icon: BrainCircuit,
    accent: '#8B5CF6',
    bg: '#F3EEFF',
    topics: [
      {
        name: 'Introduction to AI | GEN AI | LLM',
        desc: 'Kick off your AI journey. Understand what Artificial Intelligence, Generative AI, and Large Language Models are — and how they are reshaping the world around us.',
        video: 'https://youtu.be/wIRaMYoM7HI',
      },
      {
        name: 'Chat Model & How to Create a Chat Model',
        desc: 'Dive into Chat Models — learn how they work under the hood, then build your very own chat model from scratch using Google Colab.',
        video: 'https://youtu.be/mIWPYdAluIU',
        colab: 'https://colab.research.google.com/drive/1UazQv5iSBahiLebTefZldrm77u9uxLa4?usp=sharing',
        chapterTitle: 'What is an LLM (Large Language Model)?',
        chapter: {
          sections: [
            {
              heading: 'What is an LLM (Large Language Model)?',
              paragraphs: [
                'A Large Language Model (LLM) is an advanced AI model trained on vast amounts of text data.',
                'It is designed to understand, process, and generate human-like language.',
                '👉 In simple terms:',
                'An LLM learns from massive datasets so it can answer questions, generate content, and assist in conversations based on its training.',
              ],
            },
            {
              heading: 'Who Provides LLM Models?',
              paragraphs: [
                'There are two main types of LLM providers:',
              ],
              bullets: [
                {
                  title: 'Direct Providers',
                  body: 'These are companies that build and train their own LLM models.',
                  subBullets: ['OpenAI (GPT models)', 'Meta (LLaMA models)', 'Google (Gemini)'],
                },
                {
                  title: 'Third-Party Providers',
                  body: 'These platforms provide access to multiple LLM models through a single API.',
                  subBullets: ['Groq (via ChatGroq)', 'OpenRouter', 'Hugging Face'],
                },
              ],
              note: '👉 These providers help developers easily switch between models without building them from scratch.',
            },
            {
              heading: 'What is a Framework?',
              paragraphs: [
                'A framework is a pre-defined structure that helps developers build applications faster and efficiently.',
                '👉 In AI development:',
                'We use frameworks to simplify tasks like:',
              ],
              bulletsSimple: ['Model integration', 'Prompt handling', 'Workflow management'],
              note: 'Popular AI Framework: LangChain',
              trailingParagraphs: [
                'LangChain helps in building AI applications such as:',
              ],
              bulletsTrailing: ['Chatbots', 'AI Agents', 'RAG (Retrieval-Augmented Generation) systems'],
            },
            {
              heading: 'What is a Chat Model?',
              paragraphs: [
                'A Chat Model is an interface that allows users to interact with an LLM by asking questions and receiving responses.',
                '👉 It works like:',
              ],
              bulletsSimple: ['Input → Question', 'Output → AI-generated Answer'],
            },
            {
              heading: 'How to Create a Chat Model (Using LangChain + Groq)',
              paragraphs: ['Below is a simple example of creating and using a chat model:'],
              code: `from langchain_groq import ChatGroq

# Initialize the LLM
llm = ChatGroq(
    model="llama-3.1-8b-instant",
    groq_api_key="YOUR_API_KEY",
    temperature=0
)

# Ask a question
result = llm.invoke("What is the capital of India?")

# Print the response
print(result.content)`,
            },
            {
              heading: 'Explanation of the Code',
              bulletsSimple: [
                'Import Library → Load ChatGroq from LangChain',
                'Initialize Model → Provide model name and API key',
                'Set Temperature → Controls randomness (0 = more accurate, less creative)',
                'Invoke Method → Send question to the model',
                'Get Response → Print the AI-generated answer',
              ],
            },
          ],
        },
      },
      {
        name: 'Chat Model - Gemini',
        desc: 'Explore Google\'s Gemini model and learn how to build a chat model using Gemini through hands-on coding in Google Colab.',
        video: 'https://youtu.be/oE9X-L-C5EU',
        colab: 'https://colab.research.google.com/drive/1gVA48wBYepKJKbUwmiT84CKTHQDW5Ido?usp=sharing',
      },
      {
        name: 'Prompt & Types of Prompt',
        desc: 'Master the art of prompting. Learn what prompts are, the different types of prompts, and how to craft effective prompts to get the best results from AI models.',
        video: 'https://youtu.be/zjLXFO4Ht3Y',
      },
      {
        name: 'System Prompt & User Prompt with Chat Model',
        desc: 'Understand the difference between System Prompts and User Prompts, and learn how to use them together to control chat model behaviour effectively.',
        video: 'https://youtu.be/6DrJuI3FeHQ',
        colab: 'https://colab.research.google.com/drive/1sbgnqahlBYBGYtpaf2BeI-3N_dHb-XnJ?usp=sharing',
        colab2: 'https://colab.research.google.com/drive/1daoTGOVrFYc0ngtiN2WJRD9HEZMGSlU0?usp=sharing',
      },
      {
        name: 'AI Prompt - Memory',
        desc: 'Learn how AI models remember conversations using Memory. Understand different types of memory in LangChain and build a chatbot that actually remembers what you said.',
        video: 'https://youtu.be/O2Xk6WBZHxs',
        colab: 'https://colab.research.google.com/drive/1DfVFXO9RXL7B9QR9n52RXbkwKBbgbuqg?usp=sharing',
        chapterTitle: 'AI Prompt — Memory in LangChain',
        chapter: {
          sections: [
            {
              heading: 'What is Memory in AI?',
              paragraphs: [
                'By default, every time you send a message to a chat model, it treats it as a brand-new conversation — it has no idea what you said before.',
                'Memory solves this. It allows an AI model to remember previous messages and use that context when generating the next response.',
                '👉 In simple terms:',
                'Without memory → The model forgets everything after each reply.',
                'With memory → The model carries the conversation history forward, just like a real chat.',
              ],
            },
            {
              heading: 'Why is Memory Important?',
              paragraphs: [
                'Imagine asking an AI "My name is Ravi" and then asking "What is my name?" — without memory, it would say it does not know.',
                'Memory makes AI feel natural and context-aware, which is essential for:',
              ],
              bulletsSimple: [
                'Chatbots that hold meaningful conversations',
                'Assistants that remember your preferences',
                'AI Agents that track task progress across multiple steps',
              ],
            },
            {
              heading: 'Types of Memory in LangChain',
              paragraphs: ['LangChain provides several memory types depending on how much history you want to keep:'],
              bullets: [
                {
                  title: 'ChatMessageHistory',
                  body: 'The simplest form. Manually stores a list of Human and AI messages. You control what gets added.',
                  subBullets: ['Full control over history', 'No automatic management', 'Best for learning the concept'],
                },
                {
                  title: 'ConversationBufferMemory',
                  body: 'Automatically stores the entire conversation history and passes it to the model on every call.',
                  subBullets: ['Easy to use', 'Grows with every message', 'Can become large over long conversations'],
                },
                {
                  title: 'ConversationBufferWindowMemory',
                  body: 'Stores only the last K messages. Older messages are dropped automatically.',
                  subBullets: ['Keeps memory size under control', 'Good for long-running chatbots', 'Set k=5 to keep last 5 exchanges'],
                },
                {
                  title: 'ConversationSummaryMemory',
                  body: 'Uses an LLM to summarise the conversation so far instead of storing every message.',
                  subBullets: ['Very token-efficient', 'Handles very long conversations', 'Slightly slower due to summarisation step'],
                },
              ],
            },
            {
              heading: 'How Memory Works — Step by Step',
              paragraphs: ['Here is the flow when memory is used with a chat model:'],
              bulletsSimple: [
                'Step 1 → User sends a message',
                'Step 2 → Memory retrieves the conversation history so far',
                'Step 3 → History + new message are sent together to the LLM',
                'Step 4 → LLM generates a response with full context',
                'Step 5 → Memory saves the new message and response for the next turn',
              ],
            },
            {
              heading: 'Code — Manual Memory with ChatMessageHistory',
              paragraphs: ['This example shows the most fundamental way to manage memory manually:'],
              code: `from langchain_groq import ChatGroq
from langchain_core.messages import HumanMessage, AIMessage

llm = ChatGroq(
    model="llama-3.1-8b-instant",
    groq_api_key="YOUR_API_KEY"
)

# Build conversation history manually
history = []

# Turn 1
history.append(HumanMessage(content="My name is Ravi."))
response = llm.invoke(history)
history.append(AIMessage(content=response.content))
print("AI:", response.content)

# Turn 2 — model remembers the name
history.append(HumanMessage(content="What is my name?"))
response = llm.invoke(history)
history.append(AIMessage(content=response.content))
print("AI:", response.content)`,
            },
            {
              heading: 'Code — Automatic Memory with ConversationChain',
              paragraphs: ['LangChain can manage memory automatically using ConversationBufferMemory:'],
              code: `from langchain_groq import ChatGroq
from langchain.memory import ConversationBufferMemory
from langchain.chains import ConversationChain

llm = ChatGroq(
    model="llama-3.1-8b-instant",
    groq_api_key="YOUR_API_KEY"
)

memory = ConversationBufferMemory()

conversation = ConversationChain(
    llm=llm,
    memory=memory,
    verbose=True   # Shows what history is being sent
)

# The chain handles history automatically
print(conversation.predict(input="Hi, I am Priya."))
print(conversation.predict(input="What is my name?"))
print(conversation.predict(input="Tell me a joke."))`,
            },
            {
              heading: 'Explanation of the Code',
              bulletsSimple: [
                'ChatGroq → The LLM we are using (can swap with any LangChain-compatible model)',
                'ConversationBufferMemory → Stores every message automatically',
                'ConversationChain → Combines the LLM + Memory into one easy-to-use chain',
                'verbose=True → Prints the full prompt being sent so you can see history in action',
                'conversation.predict() → Sends message and gets response, memory is updated automatically',
              ],
            },
            {
              heading: 'Key Takeaway',
              paragraphs: [
                '👉 Memory is what transforms a stateless language model into a conversational AI that feels intelligent and context-aware.',
                'Start with ChatMessageHistory to understand the concept, then use ConversationBufferMemory or ConversationBufferWindowMemory in real projects.',
              ],
              note: 'Practice tip: Run the Google Colab notebook and try changing the memory type to see how the model\'s responses differ!',
            },
          ],
        },
      },
      {
        name: 'Revision — System, Human & AI Prompt',
        desc: 'Revisit and reinforce your understanding of System Prompts, Human Prompts, and AI Prompts — consolidating everything covered so far before moving to advanced topics.',
        video: 'https://youtu.be/swovoViiMjQ',
      },
      {
        name: 'Setting up AI workspace on Local',
        desc: 'Set up your local AI development workspace step by step so you can run, test, and build AI projects directly on your machine.',
        video: 'https://youtu.be/9vLWkmQhZtg',
      },
      {
        name: 'RAG Theory',
        desc: 'Understand the core theory behind Retrieval-Augmented Generation (RAG), including how retrieval and generation work together to improve LLM responses.',
        video: 'https://youtu.be/INsGGSLCcG0',
      },
      {
        name: 'Setting up PostgreSQL vector DB using Docker',
        desc: 'Learn how to set up a PostgreSQL vector database with Docker and prepare your local environment for vector-powered AI workflows.',
        video: 'https://youtu.be/ljCQgi8fmUc',
        sourceDocument: 'https://drive.google.com/file/d/1OGD_aZ4PXnCAvknityo5bAoH-ZV08tm3/view?usp=sharing',
        dockerCompose: 'https://drive.google.com/file/d/1N1xLbbxzjuJWY1C2S2oIUbs9vMRbZCY9/view?usp=sharing',
      },
      {
        name: 'Pipeline Number 01 — Embedder',
        desc: 'Build and run the Embedder pipeline (code + Docker compose + requirements).',
        video: 'https://youtu.be/JvxtO07yddQ',
        dockerCompose: '/RAG/docker-compose.yml',
        embedderFile: '/RAG/embedder.py',
        requirementsFile: '/RAG/requirments.txt',
      },
    ],
  },
];

/* ─── Topic action button ─────────────────────────────── */
const TopicButton: React.FC<{
  href?: string;
  label: string;
  icon: React.ReactNode;
  color: string;
}> = ({ href, label, icon, color }) => {
  const enabled = Boolean(href);
  const base = {
    fontWeight: 700,
    fontSize: '0.72rem',
    borderRadius: '20px',
    px: 1.5,
    py: 0.6,
    minWidth: 0,
    textTransform: 'none' as const,
    lineHeight: 1.2,
    '& .MuiButton-startIcon': { mr: 0.5 },
  };
  if (enabled) {
    return (
      <Button
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        startIcon={icon}
        sx={{
          ...base,
          bgcolor: color,
          color: '#fff',
          boxShadow: `0 3px 10px ${color}33`,
          '&:hover': { bgcolor: color, filter: 'brightness(0.92)', boxShadow: `0 5px 16px ${color}44` },
        }}
      >
        {label}
      </Button>
    );
  }
  return (
    <Button
      disabled
      startIcon={icon}
      sx={{
        ...base,
        bgcolor: '#F1F5F9',
        color: '#94A3B8',
        border: '1px solid #E5E7EB',
        '&.Mui-disabled': { color: '#94A3B8' },
      }}
    >
      {label}
    </Button>
  );
};

/* ─── Read Chapter dialog ─────────────────────────────── */
const ChapterDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  title?: string;
  chapter?: Chapter;
}> = ({ open, onClose, title, chapter }) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="md"
    PaperProps={{ sx: { borderRadius: '18px', border: '1px solid #E5E7EB' } }}
  >
    <DialogTitle sx={{ pr: 7 }}>
      <Typography sx={{ fontWeight: 900, color: '#0F172A', fontSize: '1.1rem' }}>
        {title ?? 'Read Chapter'}
      </Typography>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 12,
          top: 12,
          color: '#334155',
          border: '1px solid #E2E8F0',
          '&:hover': { bgcolor: '#F1F5F9' },
        }}
      >
        <X size={18} />
      </IconButton>
    </DialogTitle>
    <Divider />
    <DialogContent sx={{ py: 2.5 }}>
      {!chapter ? (
        <Typography sx={{ color: '#64748B' }}>No chapter content found.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
          {(chapter.sections ?? []).map((s, idx) => (
            <Box key={`${s.heading}-${idx}`}>
              <Typography sx={{ fontWeight: 900, color: '#0F172A', fontSize: '1rem', mb: 1 }}>
                {s.heading}
              </Typography>

              {(s.paragraphs ?? []).map((p, pIdx) => (
                <Typography key={`${idx}-p-${pIdx}`} sx={{ color: '#334155', lineHeight: 1.75, mb: 0.75 }}>
                  {p}
                </Typography>
              ))}

              {s.note && (
                <Typography sx={{ color: '#0F172A', fontWeight: 700, lineHeight: 1.7, mb: 0.75 }}>
                  {s.note}
                </Typography>
              )}

              {s.trailingParagraphs?.map((p, pIdx) => (
                <Typography key={`${idx}-tp-${pIdx}`} sx={{ color: '#334155', lineHeight: 1.75, mb: 0.75 }}>
                  {p}
                </Typography>
              ))}

              {Array.isArray(s.bulletsSimple) && s.bulletsSimple.length > 0 && (
                <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 1 }}>
                  {s.bulletsSimple.map((b, bIdx) => (
                    <Box component="li" key={`${idx}-bs-${bIdx}`} sx={{ color: '#334155', lineHeight: 1.75, mb: 0.5 }}>
                      {b}
                    </Box>
                  ))}
                </Box>
              )}

              {Array.isArray(s.bulletsTrailing) && s.bulletsTrailing.length > 0 && (
                <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 1 }}>
                  {s.bulletsTrailing.map((b, bIdx) => (
                    <Box component="li" key={`${idx}-bt-${bIdx}`} sx={{ color: '#334155', lineHeight: 1.75, mb: 0.5 }}>
                      {b}
                    </Box>
                  ))}
                </Box>
              )}

              {Array.isArray(s.bullets) && s.bullets.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1 }}>
                  {s.bullets.map((b, bIdx) => (
                    <Box
                      key={`${idx}-b-${bIdx}`}
                      sx={{ border: '1px solid #E5E7EB', borderRadius: '14px', p: 1.5, bgcolor: '#FFFFFF' }}
                    >
                      <Typography sx={{ fontWeight: 900, color: '#0F172A', mb: 0.5 }}>{b.title}</Typography>
                      <Typography sx={{ color: '#334155', lineHeight: 1.75, mb: 0.75 }}>{b.body}</Typography>
                      {Array.isArray(b.subBullets) && b.subBullets.length > 0 && (
                        <Box component="ul" sx={{ pl: 2.5, my: 0 }}>
                          {b.subBullets.map((sb, sbIdx) => (
                            <Box component="li" key={`${idx}-sb-${bIdx}-${sbIdx}`} sx={{ color: '#334155', lineHeight: 1.75, mb: 0.5 }}>
                              {sb}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}

              {s.code && (
                <Box
                  component="pre"
                  sx={{
                    mt: 1.25,
                    mb: 0,
                    p: 2,
                    borderRadius: '14px',
                    bgcolor: '#0B1220',
                    color: '#E2E8F0',
                    overflowX: 'auto',
                    border: '1px solid rgba(148,163,184,0.18)',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                    fontSize: '0.86rem',
                    lineHeight: 1.65,
                  }}
                >
                  {s.code}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </DialogContent>
  </Dialog>
);

/* ─── Page ────────────────────────────────────────────── */
const AITrainingDashboard: React.FC = () => {
  const [activeId, setActiveId] = useState<string>('python');
  const [chapterTopic, setChapterTopic] = useState<Topic | null>(null);
  const active = useMemo(
    () => modules.find((m) => m.id === activeId) ?? modules[0],
    [activeId],
  );
  const totalTopics = useMemo(
    () => modules.reduce((sum, m) => sum + m.topics.length, 0),
    [],
  );

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 8, md: 10 }, pb: 8, bgcolor: '#F8FAFC' }}>

      {/* ── Hero Banner ──────────────────────────────────── */}
      <Box
        sx={{
          position: 'relative',
          color: '#fff',
          py: { xs: 6, md: 8 },
          px: 2,
          mb: 6,
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0f172a 100%)',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <GraduationCap size={22} color="#93c5fd" />
              <Typography
                sx={{
                  color: '#93c5fd',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                }}
              >
                AI Training Program
              </Typography>
            </Box>
            <Typography
              component="h1"
              sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, fontWeight: 800, mb: 2, lineHeight: 1.2 }}
            >
              AI Training Dashboard
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '1rem', md: '1.1rem' },
                color: 'rgba(255,255,255,0.8)',
                lineHeight: 1.8,
                maxWidth: 640,
              }}
            >
              Three core modules — <strong style={{ color: '#fff' }}>Python</strong>,{' '}
              <strong style={{ color: '#fff' }}>Database</strong> and{' '}
              <strong style={{ color: '#fff' }}>Artificial Intelligence</strong> — that take you
              from writing your first line of code to deploying an end-to-end AI agent.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg">

        {/* ── Registration CTA ───────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
        >
          <Box
            sx={{
              position: 'relative',
              mb: 6,
              p: { xs: 3, md: 4.5 },
              borderRadius: '24px',
              background:
                'radial-gradient(900px circle at 0% 0%, rgba(139,92,246,0.35), transparent 45%), radial-gradient(700px circle at 100% 100%, rgba(6,182,212,0.30), transparent 45%), linear-gradient(135deg, #0B1120 0%, #1E1B4B 55%, #0B1120 100%)',
              color: '#fff',
              overflow: 'hidden',
              border: '1px solid rgba(148,163,184,0.18)',
              boxShadow: '0 20px 50px rgba(30,27,75,0.45)',
            }}
          >
            {/* subtle grid glow */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage:
                  'linear-gradient(rgba(148,163,184,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.08) 1px, transparent 1px)',
                backgroundSize: '34px 34px',
                maskImage: 'radial-gradient(circle at 50% 40%, #000 0%, transparent 78%)',
                pointerEvents: 'none',
              }}
            />
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 3,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 280 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.6,
                    mb: 2,
                    borderRadius: '20px',
                    bgcolor: 'rgba(139,92,246,0.18)',
                    border: '1px solid rgba(167,139,250,0.4)',
                  }}
                >
                  <Sparkles size={15} color="#C4B5FD" />
                  <Typography sx={{ color: '#C4B5FD', fontWeight: 700, fontSize: '0.72rem', letterSpacing: 0.6, textTransform: 'uppercase' }}>
                    Enrollment Open
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.35rem', md: '1.75rem' },
                    lineHeight: 1.25,
                    mb: 1.25,
                    background: 'linear-gradient(90deg, #fff 0%, #C4B5FD 55%, #67E8F9 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Become part of the AI Training Program
                </Typography>

                <Typography sx={{ color: 'rgba(226,232,240,0.85)', fontSize: { xs: '0.92rem', md: '1.02rem' }, lineHeight: 1.7, maxWidth: 620, mb: 2.25 }}>
                  Learn AI the practical way — from Python fundamentals to building real AI agents.
                  Every chapter comes with a guided video, study material, a ready-to-run Google Colab
                  notebook and a hands-on assignment — plus <strong style={{ color: '#fff' }}>dedicated live doubt-clearing classes</strong> where
                  you can get your questions answered in real time. Register now to unlock full access to the dashboard.
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {[
                    { icon: <Youtube size={14} />, label: 'Guided Videos' },
                    { icon: <Radio size={14} />, label: 'Live Doubt Classes' },
                    { icon: <FileText size={14} />, label: 'Study Material' },
                    { icon: <Code2 size={14} />, label: 'Google Colab' },
                    { icon: <Download size={14} />, label: 'Assignments' },
                    { icon: <GraduationCap size={14} />, label: 'Certificate' },
                  ].map((chip) => (
                    <Box
                      key={chip.label}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.5,
                        py: 0.7,
                        borderRadius: '20px',
                        bgcolor: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(148,163,184,0.22)',
                        color: '#E2E8F0',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        backdropFilter: 'blur(4px)',
                      }}
                    >
                      <Box sx={{ color: '#67E8F9', display: 'flex' }}>{chip.icon}</Box>
                      {chip.label}
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', md: 'flex-end' }, gap: 1 }}>
                <Button
                  component="a"
                  href={REGISTRATION_FORM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<ExternalLink size={18} />}
                  sx={{
                    background: 'linear-gradient(135deg, #8B5CF6 0%, #06B6D4 100%)',
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: '1rem',
                    borderRadius: '30px',
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 10px 28px rgba(139,92,246,0.45)',
                    '&:hover': { filter: 'brightness(1.08)', transform: 'translateY(-2px)', boxShadow: '0 14px 36px rgba(139,92,246,0.55)' },
                    transition: 'all 0.22s',
                  }}
                >
                  Register Now
                </Button>
                <Typography sx={{ color: 'rgba(148,163,184,0.85)', fontSize: '0.72rem', textAlign: { xs: 'center', md: 'right' } }}>
                  Free to register · Limited seats
                </Typography>
              </Box>
            </Box>
          </Box>
        </motion.div>

        {/* ── Module Overview Cards ──────────────────────── */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {modules.map((m, i) => {
            const Icon = m.icon;
            const isActive = m.id === activeId;
            return (
              <Grid size={{ xs: 12, md: 4 }} key={m.id}>
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.1 }}
                >
                  <motion.div whileHover={{ y: -4, transition: { duration: 0.2 } }}>
                    <Card
                      onClick={() => setActiveId(m.id)}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: '20px',
                        border: `2px solid ${isActive ? m.accent : '#E5E7EB'}`,
                        boxShadow: isActive
                          ? `0 10px 30px ${m.accent}26`
                          : '0 2px 12px rgba(0,0,0,0.05)',
                        bgcolor: '#fff',
                        overflow: 'hidden',
                        position: 'relative',
                        transition: 'border-color 0.25s, box-shadow 0.25s',
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          background: `linear-gradient(135deg, ${m.accent}12 0%, transparent 55%)`,
                          pointerEvents: 'none',
                        }}
                      />
                      <CardContent sx={{ p: 3.5, position: 'relative' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Box
                            sx={{
                              bgcolor: m.bg,
                              borderRadius: '14px',
                              p: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Icon size={26} color={m.accent} />
                          </Box>
                          <Chip
                            icon={<Layers size={14} />}
                            label={`${m.topics.length} topics`}
                            size="small"
                            sx={{
                              bgcolor: '#F1F5F9',
                              color: '#64748B',
                              fontWeight: 700,
                              fontSize: '0.72rem',
                              borderRadius: '10px',
                              border: '1px solid #E5E7EB',
                              '& .MuiChip-icon': { color: '#94A3B8' },
                            }}
                          />
                        </Box>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: '#0F172A', mb: 0.25 }}>
                          {m.title}
                        </Typography>
                        <Typography sx={{ color: m.accent, fontWeight: 700, fontSize: '0.85rem', mb: 1.25 }}>
                          {m.subtitle}
                        </Typography>
                        <Typography sx={{ color: '#475569', fontSize: '0.9rem', lineHeight: 1.7 }}>
                          {m.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>

        {/* ── Module Selector Tabs ───────────────────────── */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 4 }}>
          {modules.map((m) => {
            const Icon = m.icon;
            const isActive = m.id === activeId;
            return (
              <Button
                key={m.id}
                onClick={() => setActiveId(m.id)}
                startIcon={<Icon size={18} color={isActive ? '#fff' : m.accent} />}
                sx={{
                  bgcolor: isActive ? m.accent : '#fff',
                  color: isActive ? '#fff' : '#334155',
                  fontWeight: 800,
                  fontSize: '0.9rem',
                  borderRadius: '30px',
                  px: 2.5,
                  py: 1,
                  border: `1px solid ${isActive ? m.accent : '#E5E7EB'}`,
                  textTransform: 'none',
                  boxShadow: isActive ? `0 4px 14px ${m.accent}33` : 'none',
                  '&:hover': {
                    bgcolor: isActive ? m.accent : '#F8FAFC',
                    borderColor: m.accent,
                  },
                  transition: 'all 0.2s',
                }}
              >
                {m.title}
              </Button>
            );
          })}
        </Box>

        {/* ── Active Module Heading ──────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <Box sx={{ width: 4, height: 32, borderRadius: 4, bgcolor: active.accent }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.3rem', md: '1.6rem' }, color: '#1E293B', lineHeight: 1.2 }}>
              {active.title}
            </Typography>
            <Typography sx={{ color: '#64748B', fontSize: '0.9rem', fontWeight: 600 }}>
              {active.subtitle} · {active.topics.length} topics
            </Typography>
          </Box>
        </Box>

        {/* ── Topics Grid ────────────────────────────────── */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            <Grid container spacing={2.5}>
              {active.topics.map((t, i) => {
                const locked = !t.video && !t.video2 && !t.material && !t.assignment && !t.colab &&
                  !t.colab2 && !t.chapter && !t.sourceDocument && !t.dockerCompose &&
                  !t.embedderFile && !t.requirementsFile;
                return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={t.name}>
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: i * 0.04 }}
                  >
                    <Card
                      sx={{
                        height: '100%',
                        borderRadius: '16px',
                        border: '1px solid #E5E7EB',
                        bgcolor: '#fff',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                        transition: 'transform 0.25s, box-shadow 0.25s, border-color 0.25s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          borderColor: active.accent,
                          boxShadow: `0 10px 26px ${active.accent}1f`,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2.75 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.25 }}>
                          <Box
                            sx={{
                              width: 34,
                              height: 34,
                              flexShrink: 0,
                              borderRadius: '10px',
                              bgcolor: locked ? '#F1F5F9' : active.bg,
                              color: locked ? '#94A3B8' : active.accent,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 800,
                              fontSize: '0.85rem',
                            }}
                          >
                            {String(i + 1).padStart(2, '0')}
                          </Box>
                          <Typography sx={{ fontWeight: 800, color: '#0F172A', fontSize: '1rem', flexGrow: 1 }}>
                            {t.name}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              px: 1,
                              py: 0.4,
                              borderRadius: '20px',
                              flexShrink: 0,
                              bgcolor: locked ? '#FEF2F2' : '#ECFDF5',
                              color: locked ? '#EF4444' : '#10B981',
                              border: `1px solid ${locked ? '#FECACA' : '#A7F3D0'}`,
                              fontSize: '0.65rem',
                              fontWeight: 800,
                              textTransform: 'uppercase',
                              letterSpacing: 0.3,
                            }}
                          >
                            {locked ? <Lock size={11} /> : <Unlock size={11} />}
                            {locked ? 'Locked' : 'Open'}
                          </Box>
                        </Box>
                        <Typography sx={{ color: '#475569', fontSize: '0.875rem', lineHeight: 1.65, mb: 2 }}>
                          {t.desc}
                        </Typography>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          <TopicButton
                            href={t.video}
                            label={t.video2 ? 'Watch Video 1' : 'Watch Video'}
                            icon={<Youtube size={14} />}
                            color="#FF0000"
                          />
                          {t.video2 && (
                            <TopicButton
                              href={t.video2}
                              label="Watch Video 2"
                              icon={<Youtube size={14} />}
                              color="#FF0000"
                            />
                          )}
                          {t.chapter && (
                            <Button
                              onClick={() => setChapterTopic(t)}
                              startIcon={<BookOpen size={14} />}
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.72rem',
                                borderRadius: '20px',
                                px: 1.5,
                                py: 0.6,
                                minWidth: 0,
                                textTransform: 'none',
                                lineHeight: 1.2,
                                '& .MuiButton-startIcon': { mr: 0.5 },
                                bgcolor: '#0F172A',
                                color: '#fff',
                                boxShadow: '0 3px 10px rgba(15,23,42,0.2)',
                                '&:hover': { bgcolor: '#111827', filter: 'brightness(0.92)' },
                              }}
                            >
                              Read Chapter
                            </Button>
                          )}
                          {t.material && (
                            <TopicButton
                              href={t.material}
                              label="Student Material"
                              icon={<FileText size={14} />}
                              color={active.accent}
                            />
                          )}
                          {t.assignment && (
                            <TopicButton
                              href={t.assignment}
                              label="Download Assignment"
                              icon={<Download size={14} />}
                              color="#0F172A"
                            />
                          )}
                          {t.colab && (
                            <TopicButton
                              href={t.colab}
                              label={t.colab2 ? 'Colab 1' : 'Google Colab'}
                              icon={<Code2 size={14} />}
                              color="#F9AB00"
                            />
                          )}
                          {t.colab2 && (
                            <TopicButton
                              href={t.colab2}
                              label="Colab 2"
                              icon={<Code2 size={14} />}
                              color="#0EA5E9"
                            />
                          )}
                          {t.sourceDocument && (
                            <TopicButton
                              href={t.sourceDocument}
                              label="Source Document"
                              icon={<BookOpen size={14} />}
                              color="#334155"
                            />
                          )}
                          {t.dockerCompose && (
                            <TopicButton
                              href={t.dockerCompose}
                              label="Docker Compose"
                              icon={<Code2 size={14} />}
                              color="#111827"
                            />
                          )}
                          {t.embedderFile && (
                            <TopicButton
                              href={t.embedderFile}
                              label="Embedder.py"
                              icon={<Code2 size={14} />}
                              color="#0B1220"
                            />
                          )}
                          {t.requirementsFile && (
                            <TopicButton
                              href={t.requirementsFile}
                              label="requirements.txt"
                              icon={<Code2 size={14} />}
                              color="#0F172A"
                            />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
                );
              })}
            </Grid>
          </motion.div>
        </AnimatePresence>

        {/* ── Footer summary strip ───────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
        >
          <Box
            sx={{
              mt: 6,
              p: { xs: 3, md: 4 },
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              color: '#fff',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ bgcolor: 'rgba(147,197,253,0.15)', borderRadius: '14px', p: 1.5, display: 'flex' }}>
                <BookOpen size={26} color="#93c5fd" />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: '#fff' }}>
                  {modules.length} Modules · {totalTopics} Topics
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                  A complete path from Python basics to building AI agents.
                </Typography>
              </Box>
            </Box>
          </Box>
        </motion.div>

        <ChapterDialog
          open={Boolean(chapterTopic)}
          onClose={() => setChapterTopic(null)}
          title={chapterTopic?.chapterTitle}
          chapter={chapterTopic?.chapter}
        />

      </Container>
    </Box>
  );
};

export default AITrainingDashboard;
